import styled from "@emotion/styled";
import { IconArrowLeft } from "@kompare/kompare-ui-library";
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import aircash from "src/assets/images/Aircash.png";
import card from "src/assets/images/card.png";
import keks from "src/assets/images/keks.png";
import { formatPrice, useNavigationPath, i18n, NavigationService, PaymentOptionEnum, useGeneralSoContext, useGetOfferById, useGetSo, resolvePremiums } from 'src/common';
import { GeneralTransferPaymentInfoCard, Accordion, ArrowDownIcon, Button, Card, ContainerComponent, DocumentIcon, Flex, LoaderSquare, StepHeader, Text, WsPayIframe, DescriptionItem, VoucherCodeCard } from 'src/components';
import config from "src/config";
import { colors } from 'src/theme';
import { Helmet } from "react-helmet-async";

const LogoStyle = styled.img({
  height: '24px'
});

const ButtonContainerStyle = styled(Flex)({
  marginTop: 48,
  marginBottom: 40
});

const PriceStyle = styled(Text)({
  marginRight: 16,
  marginBottom: -4
});

const CheckoutPage = () => {
  const { soId: soIdFromUrl, offerId: offerIdFromUrl } = useParams();
  const { forwardPath, backwardPath} = useNavigationPath();

  const [openPaymentMethodIndex, setOpenPaymentMethodIndex] = useState<number | null>(null);

  const { so, refetch } = useGetSo();
  const { setSoId, setOfferId, setSoInitialData, offerId, soId } = useGeneralSoContext();
  const { offer, refetch: refetchOffer } = useGetOfferById();

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
    if (offerIdFromUrl) {
      setOfferId(offerIdFromUrl);
    }
  }, [soIdFromUrl, offerIdFromUrl]);


  useEffect(() => {
    if (!so || !offer) {
      refetch({ id: soIdFromUrl });
      refetchOffer({ id: offerIdFromUrl });
    }
  }, []);

  useEffect(() => {
    if (so) {
      setSoInitialData({
        data: so.data!,
        propertyInfo: so.productTypeSpecific?.propertyInfo,
        insuranceNeeds: so.productTypeSpecific?.insuranceNeeds,
        contractor: so.productTypeSpecific?.contractor,
        insured: so.productTypeSpecific?.insured,
      });
    }
  }, [so]);

  // Redirect if offer policy start date is in the past
  useEffect(() => {
    // const policyStartDate = offer?.productTypeSpecific?.newPolicy?.[0]?.newPolicyStartDate;
    // if (policyStartDate) {
    //   const startDate = dayjs(policyStartDate);
    //   const today = dayjs().startOf('day');
    //   if (startDate.isBefore(today) || startDate.isSame(today, 'day')) {
    //     NavigationService.navigate(AppRoutes.WEBSHOP.PROPERTY_INFO);
    //   }
    // }
  }, [offer]);

  // Listen for messages from iframe (checkout failure page)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {

      if (event.origin !== config.REACT_APP_URL) return;

      if (event.data && event.data.type === 'CHECKOUT_FAILURE') {
        const { errors } = event.data.data;

        NavigationService.navigate(`${forwardPath}?ErrorCodes=${errors}`);
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!offerId) return null;

  const { annual: yearlyPrice, monthly: monthlyPrice, installmentsTotal } = resolvePremiums(offer?.productTypeSpecific?.payment);

  const paymentOptions = [
    {
      key: 'creditCard',
      type: 'cardSingle',
      title: i18n.t('pages.checkoutPage.onetimeCard'),
      icon: <LogoStyle src={card} alt="" />,
      content: <WsPayIframe offerId={offerId!} paymentOption={PaymentOptionEnum.CreditCard} paymentType='CreditCard' />,
      price: yearlyPrice,
    },
    {
      key: 'keksPay',
      type: 'cardSingle',
      title: i18n.t('pages.checkoutPage.keksPay'),
      icon: <LogoStyle src={keks} alt="" />,
      content: config.REACT_APP_IS_DEV === "true" ?  "content" : <WsPayIframe offerId={offerId} paymentOption={PaymentOptionEnum.KeksPay} paymentType='KeksPay' />,
      price: yearlyPrice,
    },
    {
      key: 'aircash',
      type: 'cardSingle',
      title: i18n.t('pages.checkoutPage.aircash'),
      icon: <LogoStyle src={aircash} alt="" />,
      content: config.REACT_APP_IS_DEV === "true" ?  "content" : <WsPayIframe offerId={offerId} paymentOption={PaymentOptionEnum.Aircash} paymentType='Aircash' />,
      price: yearlyPrice,
    },
    {
      key: 'creditCardWithInstallments',
      type: 'cardInstallments',
      title: i18n.t('pages.checkoutPage.cardWithInstallments'),
      icon: <LogoStyle src={card} alt="" />,
      content: <WsPayIframe offerId={offerId!} paymentOption={PaymentOptionEnum.CreditCard} paymentType='CreditCard' installments />,
      price: monthlyPrice,
    },
    {
      key: 'generalTransfer',
      type: 'cashSingle',
      title: i18n.t('pages.checkoutPage.generalTransfer'),
      icon: <DocumentIcon width={30} height={30} />,
      content: (
        <GeneralTransferPaymentInfoCard offerId={offerId} transferType={'oneTime'} />
      ),
      price: yearlyPrice,
    },
    {
      key: 'generalTransfer',
      type: 'cashInstallments',
      title: i18n.t('pages.checkoutPage.generalTransferWithInstallments'),
      icon: <DocumentIcon width={30} height={30} />,
      content: (
        <GeneralTransferPaymentInfoCard offerId={offerId} transferType={'installments'} />
      ),
      price: monthlyPrice,
    },
  ].filter((pO) => offer?.productTypeSpecific?.payment.paymentMethod === pO.type);

  const handleOpenPaymentMethod = (index: number) => {
    setOpenPaymentMethodIndex(openPaymentMethodIndex === index ? null : index);
  };

  const handleBackButton = () => {
    NavigationService.navigate(backwardPath
      .replace(':soId', `${soId}`)
      .replace(':offerId', `${offerId}`)
    );
  }

  return (
    <ContainerComponent>
      <Helmet>
        <title>Plaćanje | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader/>
      {(offer && so) && (
        <Flex vertical gap={32}>
          { yearlyPrice > 100 && installmentsTotal > 100 && <VoucherCodeCard
            offerId={offerId}
            appliedVoucherCode={offer.productTypeSpecific.payment.voucher}
          />}
          <Flex vertical gap={12}>
            {paymentOptions.map((pm, index) => {
              return (
                <Card padding={16} radius={12} key={index}>
                  <Accordion
                    key={index}
                    title={<Flex justify='space-between' align='center'>
                      <Flex gap={16} align='center'>
                        {pm.icon}
                        <Text type='base' bold>
                          {pm.title}
                        </Text>
                      </Flex>
                      { pm.type.includes('cashInstallments') ? (
                        <PriceStyle type='small' color={colors.text3}>
                          <DescriptionItem
                            label={formatPrice(pm.price)}
                            value={`${formatPrice(pm.price!/12)}/mj`}
                          />
                        </PriceStyle>
                      ) : (
                        <PriceStyle type='small' color={colors.text3}>
                          {pm.price ? formatPrice(pm.price) : ''}
                        </PriceStyle>
                      )}
                    </Flex>}
                    content={pm.content}
                    open={openPaymentMethodIndex === index}
                    expandable={true}
                    expandIcon={true}
                    expandIconComponent={<ArrowDownIcon color={colors.icon1} />}
                    gap={20}
                    onChange={() => handleOpenPaymentMethod(index)}
                    isLazy={true}
                  />
                </Card>
              )
            })}

          </Flex>
          <ButtonContainerStyle justify="center" align="center">
            <Button btnType="textBlack" iconPosition="left" text={i18n.t('pages.checkoutPage.backButton')} onClick={handleBackButton}>{<IconArrowLeft />}</Button>
          </ButtonContainerStyle>
        </Flex>
      )}
      {(!offer || !so) && <LoaderSquare />}
    </ContainerComponent>
  );
}

export default CheckoutPage;
